import { z } from "zod";

// ============ Enums ============

export const sessionStatusSchema = z.enum([
  "pending",
  "active",
  "completed",
  "failed",
  "expired",
  "cancelled",
]);

export const paymentMethodSchema = z.enum(["mobile_money", "card", "qr"]);

export const customFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "email",
  "phone",
  "number",
  "date",
  "select",
]);

export const themeSchema = z.enum(["light", "dark"]);

// ============ Markets & Quotes (cross-border collection) ============

export const moneySchema = z.object({
  value: z.number(),
  currency: z.string(),
});

/** A market the merchant can collect from: payer country + charge currency. */
export const marketSchema = z.object({
  country: z.string(), // ISO 3166-1 alpha-2, e.g. "KE"
  currency: z.string(), // What a payer from this market is charged in
  // Cheapest price (in the page/session currency) payable from this market.
  // Absent for the domestic market. Moves with the exchange rate.
  min_price: moneySchema.optional().nullable(),
});

/** Ask what a phone number (binding) or a country (preview) would be charged. */
export const marketQuoteParamsSchema = z
  .object({
    phone: z.string().optional(),
    country: z.string().optional(),
    amount: z.number().positive(),
  })
  .refine((q) => !!q.phone || !!q.country, "phone or country is required");

export const marketQuoteRefusalCodeSchema = z.enum([
  "amount_below_minimum",
  "amount_above_maximum",
  "temporarily_unavailable",
  "market_unsupported",
]);

/**
 * A quote is always HTTP 200. `payable: false` carries a code and a
 * payer-safe message; `charge` is present for amount_* refusals only.
 */
export const marketQuoteSchema = z.object({
  market: z.object({ country: z.string(), currency: z.string() }),
  price: moneySchema, // The merchant's price, never changes
  charge: moneySchema.optional().nullable(), // What the payer's phone is charged
  payable: z.boolean(),
  code: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
});

// ============ Display Currency (informational secondary currency) ============

export const sessionDisplayAmountSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  rate: z.number(),
});

// ============ Line Items (E-commerce Cart) ============

export const lineItemSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  image_url: z.string().optional().nullable(),
  quantity: z.number(),
  unit_price: z.number(),
  total: z.number(),
  sku: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
});

// ============ Custom Fields (Data Collection) ============

export const customFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: customFieldTypeSchema,
  placeholder: z.string().optional(),
  required: z.boolean(),
  options: z.array(z.string()).optional(), // For select type
});

// ============ Display Settings ============

export const lineItemsStyleSchema = z.enum(["compact", "cards"]);

export const displaySettingsSchema = z.object({
  show_line_items: z.boolean().optional(),
  show_description: z.boolean().optional(),
  show_merchant_logo: z.boolean().optional(),
  theme: themeSchema.optional(),
  success_message: z.string().optional(),
  button_text: z.string().optional(),
  line_items_style: lineItemsStyleSchema.optional(),
});

// ============ Billing Info (Card Payments) ============

export const billingInfoSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postcode: z.string().min(1, "Postal code is required"),
  country: z.string().length(2, "Country must be 2-letter ISO code"),
  phone: z.string().optional(),
});

// Optional billing - allows empty object or valid billing info
export const optionalBillingSchema = z
  .union([
    billingInfoSchema,
    z.object({
      first_name: z.string().optional(),
      last_name: z.string().optional(),
      address: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postcode: z.string().optional(),
      country: z.string().optional(),
      phone: z.string().optional(),
    }),
  ])
  .optional();

// ============ Payment Attempt (for active/failed payments) ============

export const attemptStatusSchema = z.enum([
  "pending",
  "completed",
  "failed",
  "expired",
  "cancelled",
]);

export const paymentAttemptSchema = z.object({
  id: z.string(),
  attempt_number: z.number(),
  payment_method: paymentMethodSchema,
  status: attemptStatusSchema,
  customer_name: z.string().optional().nullable(),
  customer_phone: z.string().optional().nullable(),
  customer_email: z.string().optional().nullable(),
  payment_url: z.string().optional().nullable(), // Card payments
  qr_code: z.string().optional().nullable(), // QR payments
  payment_token: z.string().optional().nullable(), // QR payments - paybill number
  failure_reason: z.string().optional().nullable(),
  expires_at: z.string().optional().nullable(),
  created_at: z.string(),
  can_retry: z.boolean(),
  // What the payer's phone is charged for this attempt (cross-border payers)
  charged_amount: z.number().optional().nullable(),
  charged_currency: z.string().optional().nullable(),
});

// ============ Checkout Session Response ============

export const checkoutSessionSchema = z.object({
  // Session Info
  reference: z.string(),
  status: sessionStatusSchema,
  amount: z.number(),
  currency: z.string(),
  display_currency: z.string().optional().nullable(),
  display_amount: sessionDisplayAmountSchema.optional().nullable(),
  allowed_methods: z.array(paymentMethodSchema),
  description: z.string().optional().nullable(),
  redirect_url: z.string().optional().nullable(),
  expires_at: z.string(),

  // Pre-filled customer data
  customer_name: z.string().optional().nullable(),
  customer_phone: z.string().optional().nullable(),
  customer_email: z.string().optional().nullable(),

  // Merchant branding (from profile)
  merchant_name: z.string().optional().nullable(),
  merchant_logo: z.string().optional().nullable(),
  merchant_website: z.string().optional().nullable(),
  brand_color: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),

  // Line items
  line_items: z.array(lineItemSchema).optional().nullable(),

  // Custom fields
  custom_fields: z.array(customFieldSchema).optional().nullable(),

  // Display settings
  display: displaySettingsSchema.optional().nullable(),

  // Billing (for card payments)
  billing: billingInfoSchema.optional().nullable(),
  collect_billing: z.boolean().optional(),

  // Collection settings
  collect_email: z.boolean().optional(),

  // Custom amount
  allow_custom_amount: z.boolean().optional(),
  min_amount: z.number().optional().nullable(),
  max_amount: z.number().optional().nullable(),

  // Markets the merchant can collect from (drives phone countries + quotes)
  markets: z.array(marketSchema).optional().nullable(),

  // Payment attempts (for active sessions or retry scenarios)
  active_attempt: paymentAttemptSchema.optional().nullable(),
  latest_attempt: paymentAttemptSchema.optional().nullable(),
});

// ============ Payment Results ============

export const paymentResultSchema = z.object({
  attempt_id: z.string(),
  status: z.string(),
  payment_url: z.string().optional().nullable(), // Card payments - redirect URL
  qr_code: z.string().optional().nullable(), // QR payments - base64 image
  payment_token: z.string().optional().nullable(), // Mobile money - USSD token
  expires_at: z.string().optional().nullable(), // Attempt expiry
  // Present when the payer is in another market: the figure the USSD prompt
  // actually asks for, converted from the session's price into their currency.
  charged_amount: z.number().optional().nullable(),
  charged_currency: z.string().optional().nullable(),
});

export const paymentStatusResultSchema = z.object({
  reference: z.string().optional(),
  status: sessionStatusSchema,
  redirect_url: z.string().optional().nullable(),
});

// ============ Submit Payment Schemas ============

// Base submit payment schema
export const submitPaymentSchema = z.object({
  payment_method: paymentMethodSchema,
  amount: z.number().optional(),
  customer_name: z.string().optional(),
  customer_phone: z.string().optional(),
  customer_email: z.string().optional(),
  custom_data: z.record(z.string(), z.unknown()).optional(),
  billing: billingInfoSchema.optional(), // Required for card payments
});

// Create a dynamic schema based on session requirements
export function createSubmitPaymentSchema(options: {
  collectName: boolean;
  collectPhone: boolean;
  collectEmail: boolean;
  customAmount?: { min: number; max: number };
  customFields?: Array<{ key: string; label: string; required: boolean }>;
}) {
  const baseSchema = z.object({
    payment_method: paymentMethodSchema,
    amount: options.customAmount
      ? z
          .number({ error: "Amount is required" })
          .min(options.customAmount.min, `Amount must be at least ${options.customAmount.min}`)
          .max(options.customAmount.max, `Amount must be at most ${options.customAmount.max}`)
      : z.number().optional(),
    customer_name: options.collectName
      ? z.string().min(2, "Full name must be at least 2 characters")
      : z.string().optional(),
    customer_phone: options.collectPhone
      ? z.string().min(1, "Phone number is required")
      : z.string().optional(),
    customer_email: options.collectEmail
      ? z.string().min(1, "Email is required").email("Invalid email address")
      : z.string().optional(),
    custom_data: z.record(z.string(), z.unknown()).optional(),
    billing: optionalBillingSchema,
  });

  return baseSchema.superRefine((data, ctx) => {
    if (data.payment_method === "card") {
      const b = data.billing;
      const requiredFields = [
        { key: "first_name", label: "First name" },
        { key: "last_name", label: "Last name" },
        { key: "address", label: "Address" },
        { key: "city", label: "City" },
        { key: "state", label: "State" },
        { key: "postcode", label: "Postal code" },
        { key: "country", label: "Country" },
      ] as const;
      for (const { key, label } of requiredFields) {
        if (!b?.[key]?.trim()) {
          ctx.addIssue({
            path: ["billing", key],
            message: `${label} is required`,
            code: z.ZodIssueCode.custom,
          });
        }
      }
    }
  });
}

// ============ Type Exports ============

export type SessionStatus = z.infer<typeof sessionStatusSchema>;
export type AttemptStatus = z.infer<typeof attemptStatusSchema>;
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;
export type CustomFieldType = z.infer<typeof customFieldTypeSchema>;
export type Theme = z.infer<typeof themeSchema>;
export type LineItem = z.infer<typeof lineItemSchema>;
export type SessionDisplayAmount = z.infer<typeof sessionDisplayAmountSchema>;
export type Money = z.infer<typeof moneySchema>;
export type Market = z.infer<typeof marketSchema>;
export type MarketQuoteParams = z.infer<typeof marketQuoteParamsSchema>;
export type MarketQuote = z.infer<typeof marketQuoteSchema>;
export type MarketQuoteRefusalCode = z.infer<typeof marketQuoteRefusalCodeSchema>;
export type CustomField = z.infer<typeof customFieldSchema>;
export type DisplaySettings = z.infer<typeof displaySettingsSchema>;
export type BillingInfo = z.infer<typeof billingInfoSchema>;
export type PaymentAttempt = z.infer<typeof paymentAttemptSchema>;
export type CheckoutSession = z.infer<typeof checkoutSessionSchema>;
export type PaymentResult = z.infer<typeof paymentResultSchema>;
export type PaymentStatusResult = z.infer<typeof paymentStatusResultSchema>;
export type SubmitPaymentParams = z.infer<typeof submitPaymentSchema>;
